import { BridgeActionsBase, BridgeBase, CaseModel, SelectedApexMech, BridgeMessages as BridgeMessagesBase } from "@ortho-next/nextray-core";
import { FitboneMesh } from "../Models/AppModel";
import { ViewerStateTypes } from "../States/State";
import { Context } from "../States/Context";

/**
 * List of events that can be triggered by the angular part to interact with the three.js part.
 */
export interface BridgeActions extends BridgeActionsBase {
	/**
	 * Sets femur proximal as osteotomy site during insert osteotomy worflow.
	 */
	apexFemurProximal: null;
	/**
	 * Sets femur distal as osteotomy site during insert osteotomy worflow.
	 */
	apexFemurDistal: null;
	/**
	 * Sets tibia proximal as osteotomy site during insert osteotomy worflow.
	 */
	apexTibiaProximal: null;
	/**
	 * Ends selecting apex workflow.
	 */
	confirmApex: null;
	/**
	 * Adds or removes the plane cut along the osteotomy.
	 */
	cutEoC: boolean;
	/**
	 * Starts selecting apex workflow.
	 */
	startSelectingApex: null;
	/**
	 * Ends selecting apex workflow and restore old selected apex value.
	 */
	cancelSelectingApex: null;
	/**
	 * Cancel insertion point workflow.
	 */
	cancelInsertionPoint: null;
	/**
	 * Confirm insertion point workflow.
	 */
	confirmInsertionPoint: null;
	/**
	 * Switches the current state to RPM.
	 */
	switchToRPM: null;
	/**
	 * Starts the inserting fitbone workflow.
	 */
	startInsertingFitbone: null;
	/**
	 * Ends inserting fitbone workflow.
	 */
	confirmFitbone: null;
	/**
	 * Ends inserting fitbone workflow and restore old selected fitbone.
	 */
	cancelFitbone: null;
	/**
	 * Inserts fitbone to the scenes.
	 */
	insertFitbone: FitboneMesh;
	/**
	 * Lock/Unlock stroke movement.
	 */
	lockStroke: boolean;
	/**
	 * Generates images for print report.
	 */
	generateImagesToPrint: { w: number, h: number };
	/**
	 * Updates measures available for print report only.
	 */
	updateMeausuresOnlyPrint: null;
	/**
	* Start the crop polygon tool
	*/
	startCropPolygon: null;
	/**
	* Confirm and close the crop polygon tool
	*/
	confirmCropPolygon: null;
	/**
	* Open viewer.
	*/
	openViewer: null;
	/**
	* Close viewer.
	*/
	closeViewer: null;
	/**
	* Set viewer.
	*/
	setViewer: ViewerStateTypes;
	/**
	* Add a Blocking Screw in the active view.
	*/
	addBlockingScrew: null;
	/**
	* Delete the selected Blocking Screw in the active view.
	*/
	deleteBlockingScrew: null;
	/**
	 * Set realistic zoom in the active view.
	 */
	setRealisticZoom: number;

	/** LAYERS EVENTS **/

	/**
	 * Toggles the visibility of fitbone layer
	 */
	setVisibility_fitbone: boolean;
	/**
	 * Toggles the visibility of grid layer
	 */
	setVisibility_grid: boolean;
	/**
	 * Toggles the visibility of grid center
	 */
	setVisibility_grid_center: boolean;
	/**
	 * Toggles the visibility of plane layer
	 */
	setVisibility_plane: boolean;
}

/**
 * Messages sent from three.js part to angular part.
 */
export interface BridgeMessages extends BridgeMessagesBase {
	/**
	 * Error: osteotomy line is too long.
	 */
	osteotomyLengthError: never;
	/**
	 * Error: one of the two osteotomy doens't intersect the plane. Cannot cut the plane along osteotomy.
	 */
	cannotCut: never;
	/**
	 * AP fitbone loaded.
	 */
	fitboneLoadedAP: never;
	/**
	 * LT fitbone loaded.
	 */
	fitboneLoadedLT: never;
}

/**
 * Manages the communications between three.js and angular part.
 */
export class Bridge extends BridgeBase<BridgeActions, BridgeMessages> {
	protected declare _context: Context; // TODO put it in the package as generic

	//override
	public override mapEvent<K extends keyof BridgeActions>(type: K, args: BridgeActions[K]): void {
		switch (type) {
			case "apexFemurProximal":
				return this._context.handle("selectApex", SelectedApexMech.femurProximal);
			case "apexFemurDistal":
				return this._context.handle("selectApex", SelectedApexMech.femurDistal);
			case "apexTibiaProximal":
				return this._context.handle("selectApex", SelectedApexMech.tibiaProximal);
			case "confirmApex":
				return this._context.handle("confirmApex", null);
			case "startCropPolygon":
				return this._context.handle("startCropPolygon", null);
			case "confirmCropPolygon":
				return this._context.handle("confirmCropPolygon", null);
			case "cutEoC":
				return this._context.handle("cutEoC", args as boolean);
			case "startSelectingApex":
				return this._context.handle("startSelectingApex", null);
			case "cancelSelectingApex":
				return this._context.handle("cancelSelectingApex", null);
			case "cancelInsertionPoint":
				return this._context.handle("cancelInsertionPoint", null);
			case "confirmInsertionPoint":
				return this._context.handle("confirmInsertionPoint", null);
			case "startInsertingFitbone":
				return this._context.handle("startInsertingFitbone", null);
			case "cancelFitbone":
				return this._context.handle("cancelFitbone", null);
			case "confirmFitbone":
				return this._context.handle("confirmFitbone", null);
			case "insertFitbone":
				return this._context.handle("insertFitbone", args as FitboneMesh);
			case "lockStroke":
				return this._context.handle("lockStroke", args as boolean);
			case "updateMeausuresOnlyPrint":
				return this._context.handle("updateMeausuresOnlyPrint", null);
			case "generateImagesToPrint":
				return this._context.handle("generateImagesToPrint", args as { w: number, h: number });
			case "openViewer":
				return this._context.handle("openViewer", null);
			case "closeViewer":
				return this._context.handle("closeViewer", null);
			case "setViewer":
				return this._context.handle("setViewer", args as ViewerStateTypes);
			case "addBlockingScrew":
				return this._context.handle("addBlockingScrew", null);
			case "deleteBlockingScrew":
				return this._context.handle("deleteBlockingScrew", null);
			case "setRealisticZoom":
				return this._context.handle("setRealisticZoom", args as number);
			case "setVisibility_fitbone":
				return this._context.handle("setVisibility", { key: "fitbone", value: args as boolean });
			case "setVisibility_grid":
				return this._context.handle("setVisibility", { key: "grid", value: args as boolean });
			case "setVisibility_grid_center":
				return this._context.handle("setVisibility", { key: "grid_center", value: args as boolean });
			case "setVisibility_plane":
				return this._context.handle("setVisibility", { key: "plane", value: args as boolean });
		}

		return super.mapEvent(type, args);
	}
}
